/**
 * FoodPulse — First-login Onboarding form.
 *
 * Route: /food-pulse/profile/onboarding
 * Sections (in order):
 *   1. Basic personal (avatar + name + DOB + gender + phone + email)
 *   2. Food Preferences (multi-select + allergies free text)
 *   3. Food Assistance (Yes/No + programs)
 *   4. Loyalty Programs (repeatable rows with encrypted password)
 *   5. Consents (SMS/email, deals, ambassador, volunteer, imperfect food)
 *   6. Update Profile → PATCH + POST /profile/complete → redirect to Nearby Deals
 *
 * Nag banner appears when completion_pct < 60.
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Leaf, HeartHandshake, Trophy, Bell, Info, Loader2, Plus, X, Store, Plane, ShoppingBag, Building2, Utensils, Eye, EyeOff } from "lucide-react";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const GREEN = "#0F3D2E";
const CREAM = "#FBF7EF";
const PURPLE = "#7C3AED";

const PREF_TAGS = [
  { key: "vegetarian", label: "Vegetarian" }, { key: "vegan", label: "Vegan" },
  { key: "organic", label: "Organic" }, { key: "kosher", label: "Kosher" },
  { key: "halal", label: "Halal" }, { key: "gluten_free", label: "Gluten-free" },
  { key: "dairy_free", label: "Dairy-free" }, { key: "nut_free", label: "Nut-free" },
  { key: "pork_free", label: "Pork-free" }, { key: "beef_free", label: "Beef-free" },
];
const ASSISTANCE = [
  { key: "snap", label: "SNAP" }, { key: "wic", label: "WIC" },
  { key: "school_lunch", label: "School Lunch" }, { key: "food_bank", label: "Food Bank" },
  { key: "meals_on_wheels", label: "Meals-on-Wheels" }, { key: "other", label: "Other" },
];
const LOYALTY_TYPES = [
  { key: "supermarket", label: "Supermarket", Icon: Store },
  { key: "store", label: "Store", Icon: ShoppingBag },
  { key: "airline", label: "Airline", Icon: Plane },
  { key: "restaurant", label: "Restaurant", Icon: Utensils },
  { key: "warehouse_club", label: "Warehouse Club", Icon: Building2 },
  { key: "other", label: "Other", Icon: Store },
];

function Card({ children, title, icon: Icon }) {
  return (
    <div className="rounded-2xl bg-white border p-5 mb-4" style={{ borderColor: "#E9D8B4" }}>
      <div className="flex items-center gap-2 mb-3">
        {Icon && <Icon size={18} style={{ color: GREEN }} />}
        <h2 className="text-lg font-bold" style={{ color: GREEN }}>{title}</h2>
      </div>
      {children}
    </div>
  );
}

function Chk({ checked, onChange, label, testid }) {
  return (
    <label className="flex items-center gap-2 py-1 cursor-pointer text-sm" data-testid={testid}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="w-4 h-4 accent-[#0F3D2E]" /> {label}
    </label>
  );
}

function LoyaltyRow({ row, onSave, onDelete }) {
  const [form, setForm] = useState(row);
  const [showPw, setShowPw] = useState(false);
  const [pwPlain, setPwPlain] = useState("");
  const [revealed, setRevealed] = useState(null);
  const dirty = JSON.stringify(form) !== JSON.stringify(row) || pwPlain !== "";
  const save = async () => {
    if (!form.name?.trim()) return;
    const body = { ...form };
    if (pwPlain) body.password = pwPlain;
    if (row.id) await http.patch(`/foodpulse/profile/loyalty/${row.id}`, body);
    else await http.post("/foodpulse/profile/loyalty", body);
    onSave(); setPwPlain("");
  };
  const reveal = async () => {
    const r = await http.post(`/foodpulse/profile/loyalty/${row.id}/reveal-password`);
    setRevealed(r.data.password);
    setTimeout(() => setRevealed(null), 15000);
  };
  const Icon = (LOYALTY_TYPES.find((t) => t.key === form.program_type) || LOYALTY_TYPES[0]).Icon;
  return (
    <div className="rounded-xl border p-3 mb-2" style={{ borderColor: "#E9D8B4" }} data-testid={`loyalty-row-${row.id || "new"}`}>
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <Icon size={18} style={{ color: GREEN }} />
        <select value={form.program_type} onChange={(e) => setForm({ ...form, program_type: e.target.value, program_type_other: e.target.value === "other" ? (form.program_type_other || "") : null })} className="border rounded-lg px-2 py-1 text-xs" style={{ borderColor: "#E9D8B4" }} data-testid="loyalty-type-select">
          {LOYALTY_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
        </select>
        {form.program_type === "other" && (
          <input value={form.program_type_other || ""} onChange={(e) => setForm({ ...form, program_type_other: e.target.value })} placeholder="Specify category (e.g. Hotel, Cinema, Rideshare)" className="flex-1 min-w-[160px] border rounded-lg px-2 py-1 text-xs" style={{ borderColor: PURPLE, color: PURPLE }} data-testid="loyalty-type-other-input" />
        )}
        <input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name" className="flex-1 min-w-[120px] border rounded-lg px-2 py-1 text-sm" style={{ borderColor: "#E9D8B4" }} data-testid="loyalty-name-input" />
        {row.id && <button onClick={onDelete} className="text-red-600 text-xs"><X size={16} /></button>}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <input value={form.program_number || ""} onChange={(e) => setForm({ ...form, program_number: e.target.value })} placeholder="Program number" className="border rounded px-2 py-1 text-xs" style={{ borderColor: "#E9D8B4" }} />
        <input value={form.website || ""} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="Website" className="border rounded px-2 py-1 text-xs" style={{ borderColor: "#E9D8B4" }} />
        <input value={form.user_id_at_program || ""} onChange={(e) => setForm({ ...form, user_id_at_program: e.target.value })} placeholder="Your user ID" className="border rounded px-2 py-1 text-xs" style={{ borderColor: "#E9D8B4" }} />
        <div className="flex gap-1">
          <input type={showPw ? "text" : "password"} value={pwPlain} onChange={(e) => setPwPlain(e.target.value)} placeholder={row.has_password ? "•••••• (encrypted)" : "Password (optional)"} className="flex-1 border rounded px-2 py-1 text-xs" style={{ borderColor: "#E9D8B4" }} />
          <button onClick={() => setShowPw(!showPw)} className="border rounded px-1" style={{ borderColor: "#E9D8B4", color: GREEN }} title="Show/hide typed">{showPw ? <EyeOff size={12} /> : <Eye size={12} />}</button>
          {row.has_password && <button onClick={reveal} className="border rounded px-1 text-[10px]" style={{ borderColor: GREEN, color: GREEN }} title="Reveal saved">Reveal</button>}
        </div>
      </div>
      {revealed && <div className="mt-2 text-xs bg-yellow-50 border border-yellow-200 rounded p-1">Saved password: <b>{revealed}</b> (hidden in 15s)</div>}
      {dirty && <button onClick={save} className="mt-2 rounded-full px-3 py-1 text-xs font-semibold" style={{ background: GREEN, color: CREAM }} data-testid="loyalty-save-btn">Save</button>}
    </div>
  );
}

export default function FoodPulseOnboarding() {
  const nav = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [bundle, setBundle] = useState(null);
  const [personal, setPersonal] = useState({});
  const [prefs, setPrefs] = useState({ tags: [], allergies: "" });
  const [assist, setAssist] = useState({ enrolled: false, programs: [] });
  const [consents, setConsents] = useState({ sms_email_notifications: true, deals_offers_from_vendors: true, be_ambassador: false, be_volunteer: false, accepts_imperfect_food: true });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const load = useCallback(async () => {
    const r = await http.get("/foodpulse/profile/me");
    setBundle(r.data);
    setPersonal(r.data.profile.personal || {});
    setPrefs({ tags: r.data.profile.food_prefs?.tags || [], allergies: r.data.profile.food_prefs?.allergies || "" });
    setAssist({ enrolled: !!r.data.profile.food_assistance?.enrolled, programs: r.data.profile.food_assistance?.programs || [] });
    if (r.data.profile.consents) setConsents({ ...consents, ...r.data.profile.consents });
    // eslint-disable-next-line
  }, []);
  useEffect(() => { if (user) load(); }, [user, load]);

  const toggle = (arr, key) => arr.includes(key) ? arr.filter((k) => k !== key) : [...arr, key];

  const save = async (complete) => {
    setBusy(true); setMsg("");
    try {
      await http.patch("/foodpulse/profile/me", {
        personal, food_prefs: prefs, food_assistance: assist, consents,
      });
      if (complete) {
        await http.post("/foodpulse/profile/complete");
        nav("/food-pulse/nearby-deals");
        return;
      }
      setMsg("Saved!");
      load();
    } catch (e) { setMsg(e.response?.data?.detail || "Save failed"); }
    setBusy(false);
  };

  if (authLoading) return <div className="p-10 text-center"><Loader2 className="animate-spin mx-auto" /></div>;
  if (!user) return <div className="p-10 text-center">Please sign in.</div>;
  if (!bundle) return <div className="p-10 text-center"><Loader2 className="animate-spin mx-auto" /></div>;

  const pct = bundle.completion_pct;

  return (
    <main className="min-h-screen py-8" style={{ background: CREAM }} data-testid="fp-onboarding-page">
      <div className="max-w-3xl mx-auto px-4">
        <header className="text-center mb-6">
          <h1 className="text-3xl font-black" style={{ color: PURPLE }}>Welcome to FoodPulse</h1>
          <p className="text-sm text-gray-700 mt-1 max-w-xl mx-auto">Update Your FoodPulse Profile with important information, organized in one place, you can access easily when needed.</p>
          <div className="mt-3 max-w-md mx-auto">
            <div className="h-2 rounded-full bg-white border" style={{ borderColor: "#E9D8B4" }}>
              <div className="h-full rounded-full" style={{ width: `${pct}%`, background: `linear-gradient(90deg,${GREEN},${PURPLE})` }} />
            </div>
            <div className="text-xs text-gray-600 mt-1" data-testid="pct-label">{pct}% complete — the more you share, the smarter your deals</div>
          </div>
        </header>

        <Card title="About you" icon={CheckCircle2}>
          <div className="grid grid-cols-2 gap-2">
            <input value={personal.first_name || ""} onChange={(e) => setPersonal({ ...personal, first_name: e.target.value })} placeholder="First name" className="border rounded-lg px-3 py-2 text-sm" style={{ borderColor: "#E9D8B4" }} data-testid="first-name-input" />
            <input value={personal.last_name || ""} onChange={(e) => setPersonal({ ...personal, last_name: e.target.value })} placeholder="Last name" className="border rounded-lg px-3 py-2 text-sm" style={{ borderColor: "#E9D8B4" }} data-testid="last-name-input" />
            <select value={personal.gender || ""} onChange={(e) => setPersonal({ ...personal, gender: e.target.value })} className="border rounded-lg px-3 py-2 text-sm" style={{ borderColor: "#E9D8B4" }} data-testid="gender-select">
              <option value="">Gender</option><option>Male</option><option>Female</option><option>Non-binary</option><option>Prefer not to say</option>
            </select>
            <input type="date" value={personal.dob || ""} onChange={(e) => setPersonal({ ...personal, dob: e.target.value })} className="border rounded-lg px-3 py-2 text-sm" style={{ borderColor: "#E9D8B4" }} data-testid="dob-input" />
            <div className="col-span-2 flex gap-2">
              <input value={personal.phone_country_code || "+1"} onChange={(e) => setPersonal({ ...personal, phone_country_code: e.target.value })} className="w-16 border rounded-lg px-2 py-2 text-sm" style={{ borderColor: "#E9D8B4" }} />
              <input value={personal.phone_number || ""} onChange={(e) => setPersonal({ ...personal, phone_number: e.target.value })} placeholder="Phone" className="flex-1 border rounded-lg px-3 py-2 text-sm" style={{ borderColor: "#E9D8B4" }} data-testid="phone-input" />
            </div>
            <input value={personal.email || ""} disabled className="col-span-2 border rounded-lg px-3 py-2 text-sm bg-gray-50" style={{ borderColor: "#E9D8B4" }} />
          </div>
        </Card>

        <Card title="Food Preferences" icon={Leaf}>
          <div className="grid grid-cols-2 gap-1 mb-3">
            {PREF_TAGS.map((t) => (
              <Chk key={t.key} checked={prefs.tags.includes(t.key)} onChange={() => setPrefs({ ...prefs, tags: toggle(prefs.tags, t.key) })} label={t.label} testid={`pref-${t.key}`} />
            ))}
          </div>
          <textarea rows="2" value={prefs.allergies || ""} onChange={(e) => setPrefs({ ...prefs, allergies: e.target.value })} placeholder="Allergies, sensitivities, etc." className="w-full border rounded-lg px-3 py-2 text-sm" style={{ borderColor: "#E9D8B4" }} data-testid="allergies-input" />
        </Card>

        <Card title="Food Assistance Program" icon={HeartHandshake}>
          <label className="flex items-center gap-3 mb-3">
            <span className="text-sm">Need Food Assistance?</span>
            <button onClick={() => setAssist({ ...assist, enrolled: true })} className={`px-3 py-1 rounded-full text-xs font-semibold border ${assist.enrolled ? "bg-[#0F3D2E] text-[#FBF7EF]" : ""}`} style={!assist.enrolled ? { borderColor: GREEN, color: GREEN } : {}} data-testid="assist-yes">Yes</button>
            <button onClick={() => setAssist({ enrolled: false, programs: [] })} className={`px-3 py-1 rounded-full text-xs font-semibold border ${!assist.enrolled ? "bg-[#0F3D2E] text-[#FBF7EF]" : ""}`} style={assist.enrolled ? { borderColor: GREEN, color: GREEN } : {}} data-testid="assist-no">No</button>
          </label>
          {assist.enrolled && (
            <div className="grid grid-cols-2 gap-1">
              {ASSISTANCE.map((a) => (
                <Chk key={a.key} checked={assist.programs.includes(a.key)} onChange={() => setAssist({ ...assist, programs: toggle(assist.programs, a.key) })} label={a.label} testid={`assist-${a.key}`} />
              ))}
            </div>
          )}
        </Card>

        <Card title="Loyalty Programs" icon={Trophy}>
          {(bundle.loyalty || []).map((r) => <LoyaltyRow key={r.id} row={r} onSave={load} onDelete={async () => { await http.delete(`/foodpulse/profile/loyalty/${r.id}`); load(); }} />)}
          <LoyaltyRow row={{ program_type: "supermarket" }} onSave={load} />
        </Card>

        <Card title="Preferences & Consent" icon={Bell}>
          <Chk checked={consents.sms_email_notifications} onChange={(v) => setConsents({ ...consents, sms_email_notifications: v })} label="Accept SMS/Email notifications" testid="consent-notif" />
          <Chk checked={consents.deals_offers_from_vendors} onChange={(v) => setConsents({ ...consents, deals_offers_from_vendors: v })} label="Get deals & offers from stores/vendors" testid="consent-deals" />
          <Chk checked={consents.be_ambassador} onChange={(v) => setConsents({ ...consents, be_ambassador: v })} label="Be a FoodPulse Ambassador" testid="consent-ambassador" />
          <div className="flex items-center gap-2">
            <Chk checked={consents.be_volunteer} onChange={(v) => setConsents({ ...consents, be_volunteer: v })} label="Be a FoodPulse Volunteer" testid="consent-volunteer" />
            <button onClick={() => alert("Volunteers help package surprise boxes, distribute donation runs, and validate at-risk lots at partner stores.")} className="rounded-full px-2 py-0.5 text-[10px] font-bold border" style={{ borderColor: PURPLE, color: PURPLE }} data-testid="volunteer-info"><Info size={10} className="inline mr-1" />Info</button>
          </div>
          <Chk checked={consents.accepts_imperfect_food} onChange={(v) => setConsents({ ...consents, accepts_imperfect_food: v })} label="Accept cosmetically imperfect or near-expiry but edible food at a cheaper price?" testid="consent-imperfect" />
        </Card>

        {msg && <div className="text-sm text-center text-gray-700 mb-3" data-testid="save-msg">{msg}</div>}

        <div className="flex gap-2 sticky bottom-3">
          <button onClick={() => save(false)} disabled={busy} className="flex-1 rounded-full px-4 py-3 font-semibold text-sm border" style={{ borderColor: GREEN, color: GREEN, background: "white" }} data-testid="save-later-btn">Save & complete later</button>
          <button onClick={() => save(true)} disabled={busy || pct < 30} className="flex-1 rounded-full px-4 py-3 font-semibold text-sm disabled:opacity-50" style={{ background: PURPLE, color: "white" }} data-testid="update-profile-btn">
            {busy ? <Loader2 className="animate-spin inline" size={14} /> : "Update Profile"}
          </button>
        </div>
      </div>
    </main>
  );
}
